import React from "react";
import {
  ShoppingBag,
  Film,
  Landmark,
  Trees,
  Train,
  Bus,
  Plane,
} from "lucide-react";

// Import local images
import temple from "./Img/temple.jpg";
import avm from "./Img/avm.jpg";
import vijayamall from "./Img/Nexus_Vijaya.jpg";
import panagal from "./Img/Panagal_Park.jpg";
import guindy from "./Img/Guindy.webp";

function NearbySection() {
  return (
    <section className="py-16 bg-gray-50">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            Prime Location in the Heart of Chennai
          </h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            Karuna Regent is perfectly situated in Saligramam/Vadapalani — close to shopping, landmarks, temples, and excellent transport links.
          </p>
        </div>

        {/* Nearby Notable Locations */}
        <div className="mb-16">
          <h3 className="text-2xl font-semibold text-gray-900 mb-8 text-center">
            Nearby Notable Locations
          </h3>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Forum Vijaya Mall */}
            <div className="bg-white rounded-xl shadow-md overflow-hidden hover:shadow-2xl transition-all duration-500 group">
              <div className="h-48 overflow-hidden bg-gray-200">
                <img
                  src={vijayamall}
                  alt="Forum Vijaya Mall Chennai"
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                  loading="lazy"
                  onError={(e) => {
                    e.currentTarget.src =
                      "https://via.placeholder.com/600x400/3B82F6/white?text=Forum+Vijaya+Mall";
                  }}
                />
              </div>
              <div className="p-6">
                <div className="flex items-center gap-2 text-blue-600 mb-2">
                  <ShoppingBag className="w-5 h-5" />
                  <h4 className="font-semibold text-lg">Forum Vijaya Mall</h4>
                </div>
                <p className="text-gray-600">Premium shopping & entertainment destination</p>
                <p className="text-gray-800 font-medium mt-2">~0.5 km (5–10 min walk)</p>
              </div>
            </div>

            {/* AVM Studios */}
            <div className="bg-white rounded-xl shadow-md overflow-hidden hover:shadow-2xl transition-all duration-500 group">
              <div className="h-48 overflow-hidden bg-gray-200">
                <img
                  src={avm}
                  alt="AVM Studios Vadapalani"
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                  loading="lazy"
                  onError={(e) => {
                    e.currentTarget.src =
                      "https://via.placeholder.com/600x400/8B5CF6/white?text=AVM+Studios";
                  }}
                />
              </div>
              <div className="p-6">
                <div className="flex items-center gap-2 text-purple-600 mb-2">
                  <Film className="w-5 h-5" />
                  <h4 className="font-semibold text-lg">AVM Studios</h4>
                </div>
                <p className="text-gray-600">Historic South Indian film production hub</p>
                <p className="text-gray-800 font-medium mt-2">Right opposite the hotel</p>
              </div>
            </div>

            {/* Vadapalani Murugan Temple */}
            <div className="bg-white rounded-xl shadow-md overflow-hidden hover:shadow-2xl transition-all duration-500 group">
              <div className="h-48 overflow-hidden bg-gray-200">
                <img
                  src={temple}
                  alt="Vadapalani Andavar Murugan Temple"
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                  loading="lazy"
                  onError={(e) => {
                    e.currentTarget.src =
                      "https://via.placeholder.com/600x400/F97316/white?text=Vadapalani+Murugan+Temple";
                  }}
                />
              </div>
              <div className="p-6">
                <div className="flex items-center gap-2 text-orange-600 mb-2">
                  <Landmark className="w-5 h-5" />
                  <h4 className="font-semibold text-lg">Vadapalani Murugan Temple</h4>
                </div>
                <p className="text-gray-600">Popular Arulmigu Vadapalani Andavar Temple</p>
                <p className="text-gray-800 font-medium mt-2">~1 km (12–15 min walk)</p>
              </div>
            </div>

            {/* Panagal Park & T. Nagar */}
            <div className="bg-white rounded-xl shadow-md overflow-hidden hover:shadow-2xl transition-all duration-500 group">
              <div className="h-48 overflow-hidden bg-gray-200">
                <img
                  src={panagal}
                  alt="Panagal Park and T. Nagar shopping"
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                  loading="lazy"
                  onError={(e) => {
                    e.currentTarget.src =
                      "https://via.placeholder.com/600x400/EC4899/white?text=T.+Nagar";
                  }}
                />
              </div>
              <div className="p-6">
                <div className="flex items-center gap-2 text-pink-600 mb-2">
                  <ShoppingBag className="w-5 h-5" />
                  <h4 className="font-semibold text-lg">Panagal Park & T. Nagar</h4>
                </div>
                <p className="text-gray-600">Bustling market for sarees, jewellery & textiles</p>
                <p className="text-gray-800 font-medium mt-2">~3–4 km</p>
              </div>
            </div>

            {/* Guindy National Park */}
            <div className="bg-white rounded-xl shadow-md overflow-hidden hover:shadow-2xl transition-all duration-500 group">
              <div className="h-48 overflow-hidden bg-gray-200">
                <img
                  src={guindy}
                  alt="Guindy National Park Chennai"
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                  loading="lazy"
                  onError={(e) => {
                    e.currentTarget.src =
                      "https://via.placeholder.com/600x400/10B981/white?text=Guindy+National+Park";
                  }}
                />
              </div>
              <div className="p-6">
                <div className="flex items-center gap-2 text-green-600 mb-2">
                  <Trees className="w-5 h-5" />
                  <h4 className="font-semibold text-lg">Guindy National Park</h4>
                </div>
                <p className="text-gray-600">Urban forest & children's park in Chennai</p>
                <p className="text-gray-800 font-medium mt-2">~8–10 km (20–30 min drive)</p>
              </div>
            </div>
          </div>
        </div>

        {/* Travel & Connectivity */}
        <div>
          <h3 className="text-2xl font-semibold text-gray-900 mb-8 text-center">
            Excellent Travel Connectivity
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-white rounded-xl shadow-md p-6 text-center hover:shadow-lg transition">
              <Train className="w-12 h-12 text-blue-600 mx-auto mb-4" />
              <h4 className="font-semibold mb-2">Vadapalani Metro Station</h4>
              <p className="text-gray-600 text-sm">~0.8 km</p>
              <p className="text-gray-500 text-xs mt-1">Green Line – Chennai Metro</p>
            </div>

            <div className="bg-white rounded-xl shadow-md p-6 text-center hover:shadow-lg transition">
              <Bus className="w-12 h-12 text-blue-600 mx-auto mb-4" />
              <h4 className="font-semibold mb-2">CMBT Koyambedu</h4>
              <p className="text-gray-600 text-sm">~3 km</p>
              <p className="text-gray-500 text-xs mt-1">Asia's largest bus terminus</p>
            </div>

            <div className="bg-white rounded-xl shadow-md p-6 text-center hover:shadow-lg transition">
              <Plane className="w-12 h-12 text-blue-600 mx-auto mb-4" />
              <h4 className="font-semibold mb-2">Chennai International Airport</h4>
              <p className="text-gray-600 text-sm">~10–11 km</p>
              <p className="text-gray-500 text-xs mt-1">25–35 min by car/taxi</p>
            </div>

            <div className="bg-white rounded-xl shadow-md p-6 text-center hover:shadow-lg transition">
              <Train className="w-12 h-12 text-blue-600 mx-auto mb-4" />
              <h4 className="font-semibold mb-2">Chennai Central / Egmore</h4>
              <p className="text-gray-600 text-sm">~8–11 km</p>
              <p className="text-gray-500 text-xs mt-1">Major railway stations</p>
            </div>
          </div>
        </div>

        {/* Google Maps Embed */}
        <div className="mt-16">
          <p className="text-center text-gray-700 mb-6 text-lg font-medium">
            Find us on the map
          </p>
          <div className="mx-auto max-w-5xl rounded-xl overflow-hidden shadow-2xl border border-gray-200">
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3886.7848769191046!2d80.20459079999999!3d13.049360799999999!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3a5266c385e2ac29%3A0xa63092c438ca755a!2sKaruna%20Regent!5e0!3m2!1sen!2sin!4v1768198355100!5m2!1sen!2sin"
              width="100%"
              height="480"
              style={{ border: 0 }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="Karuna Regent Hotel Location - Vadapalani, Chennai"
            ></iframe>
          </div>
        </div>
      </div>
    </section>
  );
}

export default NearbySection;